import type { InventoryLevel } from '@interfaces/ILogistics';
import type { ISiteMapPoint } from '@interfaces/ISiteMap';
import { fold } from '../../units';

export interface IAreaStockRow {
  materialName: string;
  level: InventoryLevel;
}

export const pointsAttr = (points: ISiteMapPoint[]): string =>
  points.map((p) => `${p.x},${p.y}`).join(' ');

/** Natural-sort comparator for floor names — numeric-aware so "Floor 10"
 *  sorts after "Floor 2" instead of before it (plain lexicographic
 *  comparison treats "1" < "2", so "Floor 10" < "Floor 2"). Drives
 *  PlanPicker's floor list, which otherwise reads out of order on any site
 *  past nine floors. */
export const compareFloorNames = (a: string, b: string): number =>
  a.localeCompare(b, undefined, { numeric: true });

/** Vertex centroid — good enough for v1 label placement (spec §4.2). */
export const labelPoint = (points: ISiteMapPoint[]): ISiteMapPoint => ({
  x: points.reduce((s, p) => s + p.x, 0) / points.length,
  y: points.reduce((s, p) => s + p.y, 0) / points.length,
});

export const distance = (a: ISiteMapPoint, b: ISiteMapPoint): number =>
  Math.hypot(a.x - b.x, a.y - b.y);

/** Clamps a plan-space point to the backend's own bound (`Coord.min(0)` in
 *  `api/schemas/site-map.schemas.ts`) — the overlay's screen→plan transform
 *  can hand back a negative coordinate when a draw click or an equipment
 *  drag lands outside the blueprint's own viewBox, and posting that
 *  unclamped 400s. */
export const clampToPlan = (p: ISiteMapPoint): ISiteMapPoint => ({
  x: Math.max(0, p.x),
  y: Math.max(0, p.y),
});

/** Folded-name → level lookup, built once per `levels` array. Callers that
 *  join MANY materials lists against the same `levels` (e.g. `MapStage`
 *  joining every area's stock on each render) should build this once via
 *  `useMemo(() => buildFoldedLevelsLookup(levels), [levels])` and pass it to
 *  `joinAreaStockFromLookup` per list, instead of calling `joinAreaStock`
 *  repeatedly — each call rebuilds the whole map from `levels`. */
export type FoldedLevelsLookup = Map<string, InventoryLevel>;

export function buildFoldedLevelsLookup(levels: InventoryLevel[]): FoldedLevelsLookup {
  return new Map(levels.map((l) => [fold(l.materialName), l]));
}

export function joinAreaStockFromLookup(
  materials: string[],
  lookup: FoldedLevelsLookup,
): IAreaStockRow[] {
  return materials.flatMap((m) => {
    const level = lookup.get(fold(m));
    return level ? [{ materialName: m, level }] : [];
  });
}

export function joinAreaStock(materials: string[], levels: InventoryLevel[]): IAreaStockRow[] {
  return joinAreaStockFromLookup(materials, buildFoldedLevelsLookup(levels));
}

/** Plan-units-per-screen-px, sane fallback when the SVG has no CTM yet
 *  (jsdom, or pre-layout). A blueprint's viewBox can be thousands of plan
 *  units wide, so screen-space affordances (draft vertex sizing, the
 *  close-the-polygon click threshold) need this to convert a fixed on-screen
 *  distance/size into the right plan-unit distance for the CURRENT scale —
 *  a bare plan-unit constant collapses to sub-pixel on a large blueprint. */
export const DEFAULT_UNITS_PER_PX = 1;

export function getUnitsPerPx(svg: SVGSVGElement | null): number {
  const ctm = svg?.getScreenCTM?.();
  return ctm && ctm.a ? 1 / ctm.a : DEFAULT_UNITS_PER_PX;
}

/** Pointer → plan user-space via the overlay SVG's CTM. Null before the SVG
 *  has a CTM (jsdom, or pre-layout) — callers ignore the event then. */
export function screenToPlan(
  svg: SVGSVGElement,
  clientX: number,
  clientY: number,
): ISiteMapPoint | null {
  const ctm = svg.getScreenCTM?.();
  if (!ctm) return null;
  const pt = new DOMPoint(clientX, clientY).matrixTransform(ctm.inverse());
  return { x: pt.x, y: pt.y };
}
